import { Button } from "@babackapp/ui/components/button";
import { FloatingBar, FloatingBarGroup } from "@babackapp/ui/components/floating-panel";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { AlertTriangleIcon, LoaderIcon, RotateCcw } from "lucide-react";
import { useUnavailableVariableIds } from "@babackapp/variables-registry";
import {
  ActionNodeDataDto,
  ActionNodeEmailConfigDto,
} from "@babackapp/private-api-client/types";
import useCurrentShopSuspense from "../hooks/use-current-shop-suspense";
import useUpdateShopTrackingPreferences
  from "@babackapp/private-api-client/client/use-update-shop-tracking-preferences";
import { useWorkflowForm } from "../context/workflows-editor-workflows-context";
import { useWorkflowsEditorConfig } from "../context/workflows-editor-config-context";
import { isValidWebhookUrl } from "../nodes/webhook-action/webhook-url";

function ConfirmationModal({
  open,
  onOpenChange,
  title,
  message,
  cancelText,
  confirmText,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  message: string;
  cancelText: string;
  confirmText: string;
  onConfirm: () => void;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-lg font-semibold mb-2">{title}</h2>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {cancelText}
          </Button>
          <Button
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function BottomBar() {
  const { t } = useTranslation("trackoffice");
  const form = useWorkflowForm();
  const { data: shop } = useCurrentShopSuspense();
  const updateTrackingPreferences = useUpdateShopTrackingPreferences();
  const { api } = useWorkflowsEditorConfig();
  const updateWorkflow = api.useUpdateWorkflow(shop.id);

  const isDirty = form.formState.isDirty;
  const isSaving = updateTrackingPreferences.isPending;
  const unavailableIds = useUnavailableVariableIds();
  const hasUnavailableVariables = unavailableIds.size > 0;

  // In v2 (useTemplatesAndWorkflowsV2) the email subject lives on the workflow's
  // send-email action, not the template. Warn if a send-email node has no
  // subject for the shop's default locale — the validation modal in the
  // customization sheet only fires once the sheet is opened, so this is the
  // catch-all on the workflow itself. No-op for v1 shops (subject on template).
  const workflow = form.watch("workflow");
  const isV2 = shop.trackingPreferences?.useTemplatesAndWorkflowsV2 === true;
  const defaultLocale =
    shop.trackingPreferences?.notificationsDefaultLocale || "en";
  const hasMissingEmailSubject =
    isV2 &&
    (workflow?.definition?.nodes ?? []).some((node) => {
      if (node.type !== "action") return false;
      const data = node.data as ActionNodeDataDto;
      if (data?.actionType !== "send_tracking_email") return false;
      const subject = (data.config as ActionNodeEmailConfigDto)?.overrides
        ?.subject as Record<string, string> | undefined;
      return !subject?.[defaultLocale]?.trim();
    });

  // The API refuses a webhook without a valid URL, but its generic error toast
  // does not say which node is wrong: block the save here instead.
  const hasInvalidWebhookUrl = (workflow?.definition?.nodes ?? []).some(
    (node) => {
      if (node.type !== "action") return false;
      const data = node.data as { actionType?: string; config?: { url?: string } };
      return (
        data?.actionType === "send_webhook" &&
        !isValidWebhookUrl(data.config?.url)
      );
    },
  );

  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const handleResetClick = () => {
    setIsResetModalOpen(true);
  };

  const handleResetConfirm = () => {
    form.reset();
  };

  const handleSave = async () => {
    const data = form.getValues().workflow;

    if (!data) return;

    try {
      const res = await updateWorkflow.mutateAsync({
        id: data.id,
        requestBody: data,
      });
      form.reset({ workflow: res });
      // Needed to force the form dirty state to be reset
      setTimeout(() => {
        form.reset(form.getValues());
      }, 0);
    } catch (error) {
      console.error("Failed to save workflow data:", error);
      // Error notification is handled by the mutation hook
    }
  };

  return (
    <>
      <div className="w-full flex flex-col items-center gap-2">
        {hasUnavailableVariables && (
          <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5 shadow-sm">
            <AlertTriangleIcon className="size-3.5 shrink-0" />
            <span>
              {t(
                "workflowsEditor.warnings.incompatibleVariables",
                "Some variables may not be available for this trigger type",
              )}
            </span>
          </div>
        )}
        {hasMissingEmailSubject && (
          <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5 shadow-sm">
            <AlertTriangleIcon className="size-3.5 shrink-0" />
            <span>
              {t(
                "workflowsEditor.warnings.missingEmailSubject",
                "One or more emails have no subject",
              )}
            </span>
          </div>
        )}
        {hasInvalidWebhookUrl && (
          <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-full px-3 py-1.5 shadow-sm">
            <AlertTriangleIcon className="size-3.5 shrink-0" />
            <span>
              {t(
                "workflowsEditor.warnings.invalidWebhookUrl",
                "A webhook has no valid URL: enter an address starting with https:// to save",
              )}
            </span>
          </div>
        )}
        <FloatingBarGroup>
          <FloatingBar>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetClick}
              className="flex rounded-full items-center gap-2 shadow-none"
            >
              <RotateCcw className="h-4 w-4" />
              {t("workflowsEditor.actions.reset", "Reset")}
            </Button>

            <AnimatePresence>
              {isDirty && (
                <motion.div
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: "auto", opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <Button
                    size="sm"
                    className="rounded-full whitespace-nowrap"
                    onClick={handleSave}
                    disabled={isSaving || hasInvalidWebhookUrl}
                  >
                    {isSaving ? (
                      <LoaderIcon className="animate-spin" />
                    ) : (
                      t("workflowsEditor.actions.save", "Save")
                    )}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </FloatingBar>
        </FloatingBarGroup>
      </div>

      <ConfirmationModal
        open={isResetModalOpen}
        onOpenChange={setIsResetModalOpen}
        title={t(
          "workflowsEditor.actions.resetModal.title",
          "Reset to default",
        )}
        message={t(
          "workflowsEditor.actions.resetModal.message",
          "All your changes will be lost. Do you want to continue?",
        )}
        cancelText={t("workflowsEditor.actions.resetModal.cancel", "Cancel")}
        confirmText={t("workflowsEditor.actions.resetModal.confirm", "Reset")}
        onConfirm={handleResetConfirm}
      />
    </>
  );
}
